import { useState, useEffect } from 'react';
import { supabase } from '@/lib/supabase';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { useToast } from '@/hooks/use-toast';
import { Users, Crown, Zap, Trash2, Key, Copy, Eye, EyeOff, Search, MessageSquare, Calendar, RefreshCw, Building, Star, Pencil, Save } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';

interface ChatbotTenant {
  id: string;
  email: string;
  contact_name: string;
  company_name: string | null;
  plan: 'free' | 'basic' | 'pro' | 'premium';
  api_key: string;
  is_active: boolean;
  stripe_customer_id: string | null;
  created_at: string;
  updated_at: string;
}

interface TenantUsage {
  tenant_id: string;
  usage_date: string;
  message_count: number;
  conversation_count: number;
}

interface TenantWithStats extends ChatbotTenant {
  stats?: {
    todayMessages: number;
    todayConversations: number;
    monthlyMessages: number;
  };
}

// API Key Cell Component
function ApiKeyCell({ apiKey }: { apiKey: string }) {
  const [showKey, setShowKey] = useState(false);
  const { toast } = useToast();

  const copyToClipboard = () => {
    navigator.clipboard.writeText(apiKey);
    toast({
      title: 'Kopiert!',
      description: 'API-Key wurde in die Zwischenablage kopiert.',
    });
  };

  return (
    <div className="space-y-1">
      <Button
        variant="ghost"
        size="sm"
        onClick={() => setShowKey(!showKey)}
        className="h-7 text-xs"
      >
        {showKey ? <EyeOff className="h-3 w-3 mr-1" /> : <Eye className="h-3 w-3 mr-1" />}
        <Key className="h-3 w-3" />
      </Button>
      {showKey && (
        <div className="flex items-center gap-1 bg-muted p-1 rounded text-xs max-w-xs">
          <code className="flex-1 truncate font-mono text-[10px]">
            {apiKey.substring(0, 24)}...
          </code>
          <Button
            variant="ghost"
            size="sm"
            onClick={copyToClipboard}
            className="h-5 w-5 p-0"
          >
            <Copy className="h-3 w-3" />
          </Button>
        </div>
      )}
    </div>
  );
}

export function ChatbotAdmin() {
  const [tenants, setTenants] = useState<TenantWithStats[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [updatingPlan, setUpdatingPlan] = useState<string | null>(null);
  const [editingTenant, setEditingTenant] = useState<TenantWithStats | null>(null);
  const [editPlan, setEditPlan] = useState<'free' | 'basic' | 'pro' | 'premium'>('free');
  const [editCompanyName, setEditCompanyName] = useState('');
  const [editContactName, setEditContactName] = useState('');
  const [savingTenant, setSavingTenant] = useState(false);
  const { toast } = useToast();

  const loadTenants = async () => {
    setLoading(true);
    try {
      // Fetch all chatbot tenants
      const { data: tenantsData, error: tenantsError } = await supabase
        .from('chatbot_tenants')
        .select('*')
        .order('created_at', { ascending: false });

      if (tenantsError) throw tenantsError;

      // Fetch today's usage
      const today = new Date().toISOString().split('T')[0];
      const { data: usageData, error: usageError } = await supabase
        .from('chatbot_usage')
        .select('*')
        .eq('usage_date', today);

      if (usageError) throw usageError;

      // Fetch this month's usage
      const firstOfMonth = new Date();
      firstOfMonth.setDate(1);
      const monthStart = firstOfMonth.toISOString().split('T')[0];
      
      const { data: monthlyUsageData, error: monthlyError } = await supabase
        .from('chatbot_usage')
        .select('*')
        .gte('usage_date', monthStart);

      if (monthlyError) throw monthlyError;

      // Combine data
      const tenantsWithStats: TenantWithStats[] = (tenantsData || []).map((tenant) => {
        const todayUsage = (usageData || []).find((u) => u.tenant_id === tenant.id);
        const monthlyUsage = (monthlyUsageData || [])
          .filter((u) => u.tenant_id === tenant.id)
          .reduce((sum, u) => sum + (u.message_count || 0), 0);

        return {
          ...tenant,
          stats: {
            todayMessages: todayUsage?.message_count || 0,
            todayConversations: todayUsage?.conversation_count || 0,
            monthlyMessages: monthlyUsage,
          },
        };
      });

      setTenants(tenantsWithStats);
    } catch (error: any) {
      console.error('Error loading chatbot tenants:', error);
      toast({
        title: 'Fehler beim Laden',
        description: error.message || 'Chatbot-Kunden konnten nicht geladen werden.',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTenants();
  }, []);

  const handlePlanChange = async (tenantId: string, newPlan: 'free' | 'basic' | 'pro' | 'premium') => {
    setUpdatingPlan(tenantId);
    try {
      const { error } = await supabase
        .from('chatbot_tenants')
        .update({
          plan: newPlan,
          updated_at: new Date().toISOString(),
        })
        .eq('id', tenantId);

      if (error) throw error;

      toast({
        title: 'Plan aktualisiert',
        description: `Plan wurde erfolgreich auf ${newPlan.toUpperCase()} geändert.`,
      });

      loadTenants();
    } catch (error: any) {
      toast({
        title: 'Fehler',
        description: error.message || 'Plan konnte nicht aktualisiert werden.',
        variant: 'destructive',
      });
    } finally {
      setUpdatingPlan(null);
    }
  };

  const handleDeleteTenant = async (tenantId: string, tenantEmail: string) => {
    try {
      // Delete in order: usage -> conversations -> knowledge -> config -> tenant
      const { error: usageError } = await supabase
        .from('chatbot_usage')
        .delete()
        .eq('tenant_id', tenantId);

      if (usageError) throw usageError;

      const { error: conversationsError } = await supabase
        .from('chatbot_conversations')
        .delete()
        .eq('tenant_id', tenantId);

      if (conversationsError) throw conversationsError;

      const { error: knowledgeError } = await supabase
        .from('chatbot_knowledge')
        .delete()
        .eq('tenant_id', tenantId);

      if (knowledgeError) throw knowledgeError;

      const { error: configError } = await supabase
        .from('chatbot_config')
        .delete()
        .eq('tenant_id', tenantId);

      if (configError) throw configError;

      const { error: tenantError } = await supabase
        .from('chatbot_tenants')
        .delete()
        .eq('id', tenantId);

      if (tenantError) throw tenantError;

      toast({
        title: 'Kunde gelöscht',
        description: `${tenantEmail} wurde erfolgreich gelöscht.`,
      });

      loadTenants();
    } catch (error: any) {
      toast({
        title: 'Fehler beim Löschen',
        description: error.message || 'Kunde konnte nicht gelöscht werden.',
        variant: 'destructive',
      });
    }
  };

  const openEditDialog = (tenant: TenantWithStats) => {
    setEditingTenant(tenant);
    setEditPlan(tenant.plan);
    setEditCompanyName(tenant.company_name || '');
    setEditContactName(tenant.contact_name);
  };

  const saveTenantChanges = async () => {
    if (!editingTenant) return;
    setSavingTenant(true);
    try {
      const { error } = await supabase
        .from('chatbot_tenants')
        .update({
          plan: editPlan,
          company_name: editCompanyName || null,
          contact_name: editContactName,
          updated_at: new Date().toISOString(),
        })
        .eq('id', editingTenant.id);

      if (error) throw error;

      toast({
        title: 'Kunde aktualisiert',
        description: `${editingTenant.email} wurde erfolgreich aktualisiert.`,
      });

      setEditingTenant(null);
      loadTenants();
    } catch (error: any) {
      toast({
        title: 'Fehler',
        description: error.message || 'Kunde konnte nicht aktualisiert werden.',
        variant: 'destructive',
      });
    } finally {
      setSavingTenant(false);
    }
  };

  const getPlanBadge = (plan: string) => {
    switch (plan) {
      case 'premium':
        return (
          <Badge className="bg-gradient-to-r from-amber-500 to-orange-600 text-white">
            <Star className="h-3 w-3 mr-1" />
            Premium
          </Badge>
        );
      case 'pro':
        return (
          <Badge className="bg-gradient-to-r from-purple-600 to-purple-700 text-white">
            <Crown className="h-3 w-3 mr-1" />
            Pro
          </Badge>
        );
      case 'basic':
        return (
          <Badge className="bg-cyan-600 text-white">
            <Zap className="h-3 w-3 mr-1" />
            Basic
          </Badge>
        );
      default:
        return <Badge variant="outline">Free</Badge>;
    }
  };

  const getPlanLimit = (plan: string) => {
    switch (plan) {
      case 'premium': return '2000/Monat';
      case 'pro': return '750/Monat';
      case 'basic': return '150/Monat';
      default: return '10/Tag';
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('de-DE', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  };

  const filteredTenants = tenants.filter(
    (tenant) =>
      tenant.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      tenant.contact_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (tenant.company_name && tenant.company_name.toLowerCase().includes(searchTerm.toLowerCase())) ||
      tenant.plan.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Calculate stats
  const totalTenants = tenants.length;
  const freeTenants = tenants.filter((t) => t.plan === 'free').length;
  const basicTenants = tenants.filter((t) => t.plan === 'basic').length;
  const proTenants = tenants.filter((t) => t.plan === 'pro').length;
  const premiumTenants = tenants.filter((t) => t.plan === 'premium').length;

  return (
    <>
    <div className="space-y-6">
      {/* Stats Overview */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              <Users className="h-4 w-4" />
              Gesamt Kunden
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold">{totalTenants}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Free</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-slate-600">{freeTenants}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              <Zap className="h-4 w-4 text-cyan-600" />
              Basic
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-cyan-600">{basicTenants}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              <Crown className="h-4 w-4 text-purple-600" />
              Pro
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-purple-600">{proTenants}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              <Star className="h-4 w-4 text-amber-500" />
              Premium
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-amber-500">{premiumTenants}</div>
          </CardContent>
        </Card>
      </div>

      {/* Tenants Table */}
      <Card>
        <CardHeader className="border-b">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <CardTitle className="text-xl flex items-center gap-2">
                <MessageSquare className="w-5 h-5" />
                Chatbot Kundenverwaltung
              </CardTitle>
              <CardDescription>Alle registrierten KI-Chatbot Kunden</CardDescription>
            </div>
            <Button variant="outline" size="sm" onClick={loadTenants} disabled={loading}>
              <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Aktualisieren
            </Button>
          </div>
          <div className="relative max-w-md mt-4">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Kunden suchen..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="text-center py-12 text-muted-foreground">
              <RefreshCw className="h-8 w-8 mx-auto mb-4 animate-spin" />
              <p>Lade Kunden...</p>
            </div>
          ) : filteredTenants.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <Users className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p className="text-lg font-medium">Keine Kunden gefunden</p>
              <p className="text-sm">
                {searchTerm
                  ? 'Versuchen Sie eine andere Suchanfrage.'
                  : 'Es sind noch keine Chatbot-Kunden registriert.'}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-muted/50">
                    <TableHead className="font-semibold">Kontakt</TableHead>
                    <TableHead className="font-semibold">Firma</TableHead>
                    <TableHead className="font-semibold">Plan</TableHead>
                    <TableHead className="font-semibold">Limit</TableHead>
                    <TableHead className="font-semibold">API-Key</TableHead>
                    <TableHead className="font-semibold">Heute</TableHead>
                    <TableHead className="font-semibold">Monat</TableHead>
                    <TableHead className="font-semibold">Registriert</TableHead>
                    <TableHead className="font-semibold">Aktionen</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredTenants.map((tenant) => (
                    <TableRow key={tenant.id} className="hover:bg-muted/30">
                      <TableCell>
                        <div>
                          <div className="font-medium">{tenant.contact_name}</div>
                          <div className="text-xs text-muted-foreground">{tenant.email}</div>
                        </div>
                      </TableCell>
                      <TableCell>
                        {tenant.company_name ? (
                          <div className="flex items-center gap-1">
                            <Building className="h-3 w-3 text-muted-foreground" />
                            <span className="text-sm">{tenant.company_name}</span>
                          </div>
                        ) : (
                          <span className="text-xs text-muted-foreground">—</span>
                        )}
                      </TableCell>
                      <TableCell>{getPlanBadge(tenant.plan)}</TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {getPlanLimit(tenant.plan)}
                      </TableCell>
                      <TableCell>
                        <ApiKeyCell apiKey={tenant.api_key} />
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          <MessageSquare className="h-4 w-4 text-muted-foreground" />
                          <span>{tenant.stats?.todayMessages || 0}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <span className="font-medium">{tenant.stats?.monthlyMessages || 0}</span>
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        <div className="flex items-center gap-1">
                          <Calendar className="h-4 w-4" />
                          {formatDate(tenant.created_at)}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            className="h-8 w-8 p-0"
                            onClick={() => openEditDialog(tenant)}
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>

                          <Select
                            value={tenant.plan}
                            onValueChange={(value) =>
                              handlePlanChange(tenant.id, value as 'free' | 'basic' | 'pro' | 'premium')
                            }
                            disabled={updatingPlan === tenant.id}
                          >
                            <SelectTrigger className="w-28 h-8">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="free">Free</SelectItem>
                              <SelectItem value="basic">Basic</SelectItem>
                              <SelectItem value="pro">Pro</SelectItem>
                              <SelectItem value="premium">Premium</SelectItem>
                            </SelectContent>
                          </Select>

                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button variant="destructive" size="sm" className="h-8 w-8 p-0">
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader>
                                <AlertDialogTitle>Kunden löschen?</AlertDialogTitle>
                                <AlertDialogDescription>
                                  Sind Sie sicher, dass Sie <strong>{tenant.email}</strong> löschen möchten? 
                                  Diese Aktion kann nicht rückgängig gemacht werden. Alle Daten 
                                  (Konfiguration, Wissensbasis, Konversationen) werden gelöscht.
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>Abbrechen</AlertDialogCancel>
                                <AlertDialogAction
                                  onClick={() => handleDeleteTenant(tenant.id, tenant.email)}
                                  className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                                >
                                  Löschen
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>

      {/* Edit Dialog */}
      <Dialog open={!!editingTenant} onOpenChange={(open) => !open && setEditingTenant(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Kunde bearbeiten</DialogTitle>
            <DialogDescription>
              {editingTenant?.email}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="edit-contact-name">Kontaktname</Label>
              <Input
                id="edit-contact-name"
                value={editContactName}
                onChange={(e) => setEditContactName(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-company-name">Firmenname</Label>
              <Input
                id="edit-company-name"
                value={editCompanyName}
                onChange={(e) => setEditCompanyName(e.target.value)}
                placeholder="Optional"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-plan">Plan</Label>
              <Select value={editPlan} onValueChange={(v) => setEditPlan(v as 'free' | 'basic' | 'pro' | 'premium')}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="free">Free (10/Tag)</SelectItem>
                  <SelectItem value="basic">Basic (150/Monat)</SelectItem>
                  <SelectItem value="pro">Pro (750/Monat)</SelectItem>
                  <SelectItem value="premium">Premium (2000/Monat)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditingTenant(null)}>
              Abbrechen
            </Button>
            <Button onClick={saveTenantChanges} disabled={savingTenant}>
              <Save className="h-4 w-4 mr-2" />
              {savingTenant ? 'Speichern...' : 'Speichern'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
